import React, { useState } from "react";
import { withRouter } from "react-router-dom";
import moment from "moment";

import Filter from "./filters/filter";
import FilterEiac from "./filters/filter-eiac";
import GoBack from "../../../components/go-back";
import Tabs from "../../../components/tabs/tabs";
import LoaderSpinner from "../../../components/loader-spinner";
import LeftInfoModal from "../../../components/left-info-modal";
import NoData from "./common-components/no-data";
import { ClientsDashboardContainer } from "./clients-dashboard-style";
import { images } from "../../../assets";

import useGenerateData from "./hooks/useGenerateData";
import useGetEiacData from "./hooks/useGetEiacData";
import useGetMediatorData from "./hooks/useGetMediatorData";
import { withGeneral } from "../../../context/mixin/with-general";
import { TABPARAMNAME } from "../../../constants/clientsDashboard";
import { getTodayMonthYear, monthYearMinusOneYear } from "../../../utils/time";
import {
  formatErrorList,
  getUserPrivilegesTabs,
  isTabConfig,
  TABS_CONFIG,
} from "./dashboard-utils";
import {
  ClientsDashboardProps,
  EiacFilterType,
  FilterType,
  TabConfig,
  TabParamName,
  TabsConfig,
} from "./clients-dashboard-types";

const ClientsDashboard = ({ mediator, location, history, user }: ClientsDashboardProps) => {
  const userTabsConfig: Partial<TabsConfig> = getUserPrivilegesTabs(user);
  const userTabsKeys = Object.keys(userTabsConfig) as TabParamName[];
  const userTabsNames = userTabsKeys.map((key) => TABS_CONFIG[key].tabName);

  const searchParams = new URLSearchParams(location.search);
  const tabParam = (searchParams.get("tab") as TabParamName | null) || userTabsKeys[0];
  const defaultTab =
    tabParam && userTabsConfig[tabParam]
      ? userTabsConfig[tabParam]
      : userTabsConfig[userTabsKeys[0]];
  const todayMonthYear = getTodayMonthYear();

  if (!isTabConfig(defaultTab)) {
    return <NoData message="No tiene accesso a datos del Cuadro de Mando" />;
  }

  const [currentTab, setCurrentTab] = useState(defaultTab);
  const [filter, setFilter] = useState<FilterType>({
    mediatorCode: mediator ? mediator.mediatorCode : "",
    from: monthYearMinusOneYear(todayMonthYear),
    to: todayMonthYear,
    errorFrom: "",
    errorTo: "",
  });

  const [eiacFilter, setEiacFilter] = useState<EiacFilterType>({
    mediatorCode: mediator ? mediator.mediatorCode : "",
    from: moment().format("DD/MM/YYYY"),
    to: moment().format("DD/MM/YYYY"),
    errorFrom: "",
    errorTo: "",
    fileType: "all",
  });

  const { handleOnGenerate, data, loading, error, setError } = useGenerateData({
    userTabsKeys,
    filter,
  });

  const {
    data: mediatorData,
    loading: mediatorDataLoading,
    error: mediatorDataError,
    handleGetMediatorData,
  } = useGetMediatorData({ mediatorCode: filter.mediatorCode, userTabsKeys });

  const {
    handleGetEiacData,
    data: eiacData,
    loading: eiacLoading,
    error: eiacError,
  } = useGetEiacData({ filter: eiacFilter, tabParam });

  const handleOnclickTab = (tab: string) => {
    let selectedTab: TabConfig | undefined;
    let paramName = "";

    userTabsKeys.forEach((key) => {
      const tabConfig = userTabsConfig ? userTabsConfig[key] : undefined;
      if (tabConfig && tabConfig.tabName === tab) {
        selectedTab = tabConfig;
        paramName = key;
      }
    });

    if (selectedTab) {
      searchParams.set("tab", paramName);
      history.push({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
      setCurrentTab(selectedTab);
    }
  };

  const getTabContent = (): JSX.Element | null => {
    if (loading || mediatorDataLoading || eiacLoading) {
      return (
        <div className="tab-spinner">
          <LoaderSpinner />
        </div>
      );
    }

    if (currentTab.tabName !== defaultTab.tabName) {
      // Fix para la carga de la url cuando no lleva parámetros
      setCurrentTab(defaultTab);
      return null;
    }

    if (tabParam === TABPARAMNAME.RENOVACIONES || tabParam === TABPARAMNAME.IMPAGADOS) {
      return currentTab.component({
        data: tabParam && mediatorData ? mediatorData[tabParam] : null,
        mediatorCode: filter.mediatorCode,
        error: mediatorDataError,
      });
    }

    if (tabParam === TABPARAMNAME.FICHEROS_EIAC) {
      return currentTab.component({
        data: eiacData,
        error: eiacError,
        fileType: eiacFilter.fileType,
      });
    }

    if (currentTab && currentTab.component) {
      return currentTab.component({
        data: tabParam && data ? data[tabParam] : null,
        error,
      });
    }

    return null;
  };

  const errorList = formatErrorList(error);

  return (
    <ClientsDashboardContainer>
      <div className="black-arrow-container">
        <GoBack text="Volver" />
      </div>

      <div className="main-title-container">
        <h2>Cuadro de mando</h2>
      </div>

      {tabParam === "ficheros-eiac" ? (
        <FilterEiac
          filter={eiacFilter}
          setFilter={setEiacFilter}
          mediator={mediator}
          onGenerate={handleGetEiacData}
        />
      ) : (
        <Filter
          filter={filter}
          setFilter={setFilter}
          mediator={mediator}
          onGenerate={
            currentTab.tabName === TABS_CONFIG.renovaciones.tabName ||
            currentTab.tabName === TABS_CONFIG.impagados.tabName
              ? handleGetMediatorData
              : handleOnGenerate
          }
          tab={tabParam}
        />
      )}

      <Tabs
        tabs={userTabsNames}
        currentTab={currentTab.tabName}
        onClickTab={(tab: string) => handleOnclickTab(tab as TabParamName)}
      >
        {currentTab !== undefined ? getTabContent() : null}
      </Tabs>

      <LeftInfoModal
        active={error.showError && !loading}
        className="warning"
        mainTitle="Aviso"
        mainText=""
        list={errorList}
        buttonText="Cerrar"
        img={images.IconWarningYellow}
        close={() => setError((prevError) => ({ ...prevError, showError: false }))}
        onClickAccept={() => setError((prevError) => ({ ...prevError, showError: false }))}
        noSecondaryButton
      />
    </ClientsDashboardContainer>
  );
};

export default withGeneral(withRouter(ClientsDashboard));
